import {Clone, GetEntries} from "js-vextensions";
import {GenerateUUID, observer_mgl} from "mobx-graphlink";
import React, {useState} from "react";
import {Button, Column, Row, RowLR, Select, Spinner, Text, TextInput} from "react-vcomponents";
import {ErrorBoundary, ES, RunInAction_Set, TextPlus} from "web-vcore";
import {ObjModifier} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifier.js";
import {Mod_TerrainMaterials} from "../../../../../Server/DB/mapObjects/@MapObject/ObjModifiers/Mod_TerrainMaterials.js";
import {TerrainMaterialsTab, PathTab} from "../../../../../Store/main/maps/@MapState.js";
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../../../../../Terrain/Materials/@MaterialTypes.js";
import {BUILT_IN_RECIPE_IDS} from "../../../../../Terrain/Materials/MaterialFuncRegistry.js";
import {type ResolvedRecipe} from "../../../../../Terrain/Materials/RecipeResolverCore.js";
import {MaterialBlendDefaults} from "../../../../../Terrain/Materials/@MaterialFeature.js";
import {ModifierUI_SharedProps} from "../Modifiers_CompUI.js";
import {RecipeEditor} from "./TerrainMaterials/RecipeEditor.js";
import {ResolveBuiltInRecipe} from "../../../../../Terrain/Materials/@Tests/@Utils.js";
import {GetMapObjects} from "../../../../../Server/DB/mapObjects.js";
import {ResolveMapObjectsForTerrain} from "../../../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Resolver.js";
import {collectMaterialRegions, regionContainmentDepth} from "../../../../../Terrain/Materials/Splat/MaterialRegions.js";
import {GetProcMatRecipes} from "../../../../../Server/DB/mapObjects/ProcMatAccessors.js";
import {MapSurface_current} from "../../MapSurface.js";

// TerrainMaterials_ModUI — Set9 Phase 7 editor for `Mod_TerrainMaterials`: the procedural-material
// recipe catalogue (user recipes + variant authoring). Mirrors `TerrainDefinition_ModUI.tsx`'s
// pattern: an observer_mgl component mutating the modifier via `ChangeMod`, with per-modifier
// tabs in `MapState.propertiesPanel`.
//
// Feature map (plan §9 Phase 7):
//  - "New recipe" → a user root recipe (extend of a built-in by default — a "shallow variant").
//  - Auto-generated param sliders from the RESOLVED `ParamSchema[]` (the single source of truth
//    for GLSL binding AND the UI — see plan §5.1 + `ParamSchema`).
//  - "Make variant" (extend) vs "Duplicate & detach" (fork) with the cost-model badge:
//    params-only edits are FREE (same cached program, D1); layer changes trigger a recompile.
//  - Inheritance lineage + base-drift warnings surfaced from the resolver.
//  - Set12: material REGIONS moved ONTO the paths (`ObjPath.materialFeature` — fill/stroke/
//    fillAndStroke) — the path-side UI is P7; the old Set9 region-assignment tab was deleted (§6,
//    re-authored by hand via the map-object JSON editor until then).

export const TerrainMaterials_ModUI = observer_mgl((props: {mod: ObjModifier} & ModifierUI_SharedProps)=>{
	const {modWrapper: mod, ChangeModWrapper, mapState, map, obj, ChangeObj} = props;
	const data = mod.terrainMaterials!;
	const ChangeMod = (mutator: (data: Mod_TerrainMaterials)=>any)=>ChangeModWrapper(a=>mutator(a.terrainMaterials!));
	const uiState = mapState.propertiesPanel;
	const tab = uiState.terrainMaterials_tab ?? TerrainMaterialsTab.recipes;

	const [selectedRecipeIndex, setSelectedRecipeIndex] = useState(0);
	// find... by index (if in-bounds); else 0 (KeepBetween clamps to the last entry when the list shrinks)
	const selectedRecipeIndex_final = selectedRecipeIndex.KeepBetween(0, data.userRecipes.length - 1);

	return (
		<>
			<Row mt={5}>
				<Select displayType="button bar" options={GetEntries(TerrainMaterialsTab, "ui")}
					value={tab} onChange={val=>RunInAction_Set(()=>uiState.terrainMaterials_tab = val)}/>
			</Row>
			<ErrorBoundary key={tab}>
				{tab == TerrainMaterialsTab.recipes && <>
					<Row mt={8} style={{fontWeight: "bold"}}>
						<Text>Recipes:</Text>
						<Row style={{flexWrap: "wrap", gap: 5, marginLeft: 5}}>
							{data.userRecipes.map((recipe, index)=>{
								return <Button key={index} p="2px 5px" text={recipe.name}
									style={ES(
										selectedRecipeIndex_final == index && {backgroundColor: "rgba(100,200,100,.5)"},
									)}
									onClick={()=>setSelectedRecipeIndex(index)}/>;
							})}
							<Button mdIcon="plus" title="New recipe (extend of a built-in by default)" onClick={()=>{
								// "Make variant" of the first built-in — a user recipe the user can tune.
								const baseID = BUILT_IN_RECIPE_IDS[0] ?? "dirt";
								const base = ResolveBuiltInRecipe(baseID);
								const recipe = CreateDerivedRecipe(base, baseID);
								recipe.name = "New material";
								ChangeMod(a=>a.userRecipes = [...a.userRecipes, recipe]);
							}}/>
						</Row>
					</Row>
					{selectedRecipeIndex_final <= data.userRecipes.length - 1 && data.userRecipes[selectedRecipeIndex_final] &&
						<RecipeEditor key={selectedRecipeIndex_final} mod={data} mapID={map.id}
							recipe={data.userRecipes[selectedRecipeIndex_final]}
							recipeIndex={selectedRecipeIndex_final}
							ChangeMod={ChangeMod}
							onChange={updated=>ChangeMod(a=>{a.userRecipes[selectedRecipeIndex_final] = updated})}
							onClone={()=>ChangeMod(a=>{
								const clone = Clone(a.userRecipes[selectedRecipeIndex_final]);
								clone.id = NewUserRecipeID();
								clone.name += " (clone)";
								a.userRecipes = [...a.userRecipes, clone];
							})}
							onDelete={()=>ChangeMod(a=>a.userRecipes.RemoveAt(selectedRecipeIndex_final))}/>}
					{data.userRecipes.length == 0 && <Row mt={5}><Text style={{fontSize: 12, opacity: 0.7}}>
						No user recipes yet.  Add one to derive a variant of a built-in (extend), or place a
						built-in directly via a path's material feature (Set12 — see the path's Terrain tab).
					</Text></Row>}
				</>}
				{tab == TerrainMaterialsTab.regions && <>
					<RegionsOverview_UI {...{map, mapState, obj, modWrapper: mod, ChangeObj, ChangeModWrapper}}/>
				</>}
				{tab == TerrainMaterialsTab.fallbacks && <>
					<Fallbacks_UI data={data} Change={ChangeMod} mapID={map.id}/>
				</>}
			</ErrorBoundary>
		</>
	);
});

/** Fork: one-time deep copy of the RESOLVED base as a new root recipe (baseRecipeID unset).
 *  The fork is a first-class base itself — "simple variants inheriting from my new custom recipe" works. */
export function CreateForkedRecipe(base_resolved: ResolvedRecipe, newID = NewUserRecipeID(), newName = `${base_resolved.name} (fork)`): MaterialRecipe {
	const resolved = base_resolved;
	const recipe = new MaterialRecipe({
		id: newID, name: newName,
		roughness: resolved.roughness,
		specStrength: resolved.specStrength,
		flatGeoNormal: resolved.flatGeoNormal,
		lightAltitudeOverride: resolved.lightAltitudeOverride,
		layers: resolved.layers.map(l=>new MaterialLayer({
			id: l.id, primitive: l.primitive, args: {...l.args}, blend: l.blend, blendArgs: {...l.blendArgs}, contributes: l.contributes,
		})),
		params: resolved.params.map(p=>new ParamSchema({
			key: p.key, label: p.label, kind: p.kind, min: p.min, max: p.max, default: p.default, regionModulated: p.regionModulated,
		})),
	});
	return recipe;
}

/** Convert a resolved recipe (params-only defaults already applied) back into a mutable user recipe
 *  that the UI can edit.  Used by "Make variant"/"Duplicate & detach" to seed the editor. */
export function CreateDerivedRecipe(base_resolved: ResolvedRecipe, baseID: string | undefined): MaterialRecipe {
	const recipe = new MaterialRecipe({
		id: NewUserRecipeID(),
		name: `${base_resolved.name} variant`,
		baseRecipeID: baseID, // n = a root (fork); set = an extend link
		// user will populate paramOverrides, etc. using the UI
	});
	return recipe;
}

/** A new recipe id — readable + collision-avoiding (user recipes never collide with built-ins). */
function NewUserRecipeID(): string {
	return `user_${GenerateUUID().replace(/-/g, "").slice(0, 10)}`;
}

/** Set12 P7 — the READ-ONLY overview of the map's PATH-driven material regions (the §4.6
 *  "read-only list that links to each path" mitigation for authoring living on the paths).
 *
 *  Collects the regions through the RENDERER's exact pipeline (`ResolveMapObjectsForTerrain` →
 *  `collectMaterialRegions`), so the shown dominance / nesting depth / recipe / geometry always
 *  match what the splat rasteriser actually used.  Each row links to its owning path: selecting
 *  it in the outliner, switching the properties panel to the paths tab, and focusing the camera
 *  on it (the map's existing FocusObject).  Region authoring happens on the path's "Material"
 *  tab — this list is intentionally read-only (single source of truth = the path). */
const RegionsOverview_UI = observer_mgl((props: ModifierUI_SharedProps)=>{
	const {map, mapState, obj, ChangeObj} = props;

	// The renderer's own region resolution (mobx-cached accessor — re-runs on map-object edits).
	const terrainObjects = ResolveMapObjectsForTerrain(
		GetMapObjects(map.id),
		/*resolvePathOriginPathIDs:*/ true,
		/*keepPathMeshLocalPoints:*/ false,
		/*calcPathMeshWorldPoints:*/ true,
	);
	const regions = collectMaterialRegions(terrainObjects);
	const interiorRegions = regions.filter(r=>r.pathFeature.mode != "stroke");
	// Resolve the recipe name per region (the catalogue the renderer loads).
	const catalogue = GetProcMatRecipes(map.id);
	const recipeName = (id: string)=>catalogue.find(r=>r.id == id)?.name || id;

	const goToPath = (r: {id: string})=>{
		// The owning object: the one whose path produced this region id.  Walk the packaged
		// objects + their paths; the region id is the path_id (or `${objID}_path${index}`).
		const objIndex = terrainObjects.findIndex(o=>{
			return (o.paths ?? []).some(p=>{
				const id = p.path_id || `${o.id}_path${(o.paths ?? []).indexOf(p)}`;
				return id == r.id;
			});
		});
		if (objIndex < 0) return;
		const targetObj = GetMapObjects(map.id)[objIndex];
		if (targetObj == null) return;
		// Select the object (outliner + active-object), switch the properties panel to the
		// paths tab, select the owning path (index persisted on the panel state), and focus
		// the camera on it.
		RunInAction_Set(()=>{
			mapState.propertiesPanel.path_tab = PathTab.material;
			const pathIndex = (targetObj.paths ?? []).findIndex(p=>{
				const id = p.path_id || `${targetObj.id}_path${(targetObj.paths ?? []).indexOf(p)}`;
				return id == r.id;
			});
			if (pathIndex >= 0) mapState.propertiesPanel.paths_selectedIndex = pathIndex;
		});
		RunInAction_Set(()=>{
			mapState.selectedObjects = [targetObj.id];
			mapState.activeObject = targetObj.id;
		});
		if (MapSurface_current) {
			MapSurface_current.FocusObject(targetObj);
		}
	};

	return (
		<Column mt={8}>
			<Row style={{fontWeight: "bold"}}><Text>Path-driven material regions:</Text></Row>
			<Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
				<Text>Read-only overview — material assignment lives ON each path (select a path → its "Material" tab).  Rows are sorted by resolved dominance (highest first).</Text>
			</Row>
			{regions.length == 0 && <Row mt={5}><Text style={{fontSize: 12, opacity: 0.7}}>
				No material regions yet — enable a path's "Material feature" (path → Material tab) to paint a recipe.
				(Without any, terrain renders with the map's base recipe.)
			</Text></Row>}
			{regions.map(r=>{
				const depth = regionContainmentDepth(r, interiorRegions);
				return (
					<Column key={r.id} mt={4} style={{background: "rgba(255,255,255,.08)", padding: 4, borderRadius: 4}}>
						<Row>
							<Text style={{fontWeight: "bold"}}>{recipeName(r.pathFeature.recipeID)}</Text>
							<Text ml={5} style={{fontSize: 11, opacity: 0.6}}>{r.id}</Text>
							<Button ml="auto" p="1px 8px" text="Edit on path" title="Jump to this path's Material tab"
								onClick={()=>goToPath(r)}/>
						</Row>
						<Row mt={2} style={{fontSize: 11, opacity: 0.8}}>
							<Text>Mode: {r.pathFeature.mode}</Text>
							{(r.pathFeature.mode == "stroke" || r.pathFeature.mode == "fillAndStroke") && r.pathFeature.strokeWidth != null &&
								<Text ml={5}>stroke {r.pathFeature.strokeWidth} m</Text>}
							<Text ml={5}>feather {r.pathFeature.feather ?? "map-default"}</Text>
							{r.pathFeature.opaque && <Text ml={5}>opaque</Text>}
						</Row>
						<Row mt={2} style={{fontSize: 11, opacity: 0.8}}>
							{r.pathFeature.dominance != null
								? <Text>Explicit dominance: {r.pathFeature.dominance}</Text>
								: <Text>Dominance: {r.dominance} (nesting depth {depth}{r.pathFeature.mode == "stroke" ? "; strokes above fills" : ""})</Text>}
							{r.pathFeature.edgeNoise && r.pathFeature.edgeNoise.amplitude > 0 &&
								<Text ml={5}>edge noise ±{r.pathFeature.edgeNoise.amplitude} m</Text>}
							{(r.pathFeature.masks ?? []).length > 0 &&
								<Text ml={5}>{r.pathFeature.masks!.length} mask(s)</Text>}
						</Row>
					</Column>
				);
			})}
		</Column>
	);
});

/** Map-wide fallbacks (`Mod_TerrainMaterials.baseRecipeID` + `blendDefaults`) — what applies where
 *  no path region reaches.  The base recipe is the same catalogue `GetProcMatRecipes` returns (the
 *  built-ins + this map's user recipes), so a recipe chosen here is always resolvable by the
 *  renderer.  Blend defaults are the fall-back for per-path `feather`/`featherCurve` when absent. */
const Fallbacks_UI = observer_mgl((props: {
	data: Mod_TerrainMaterials;
	Change: (mutator: (data: Mod_TerrainMaterials)=>any)=>void;
	mapID: string;
})=>{
	const {data, Change, mapID} = props;
	const ChangeBlend = (mutator: (b: MaterialBlendDefaults)=>any)=>{
		Change(a=>{
			// Mutate a clone so mobx tracks the nested change; absent blendDefaults gets created.
			const next = Clone(a.blendDefaults ?? new MaterialBlendDefaults());
			mutator(next);
			a.blendDefaults = next;
		});
	};

	// The recipe catalogue — the same ids the renderer loads, so an id picked here never
	// resolves to a dangling reference at render time.
	const recipes = GetProcMatRecipes(mapID);

	return (
		<Column mt={8}>
			<Row style={{fontWeight: "bold"}}><Text>Base recipe (map-wide fallback):</Text></Row>
			<Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
				<Text>Where no path region reaches, terrain renders with this recipe.  The options are the
					built-ins + this map's user recipes (the same set the renderer loads).</Text>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					The recipe used wherever no material region (path feature) applies.  Missing/broken ids
					are a data error at render time (the renderer falls back to a default rather than crash).
				`.AsMultiline(0)}>Base recipe:</TextPlus>
				<Select ml={5} displayType="button bar" options={recipes.map(r=>({name: r.name || r.id, value: r.id}))}
					value={data.baseRecipeID} onChange={val=>Change(a=>a.baseRecipeID = val as string)}/>
			</Row>

			<Row mt={10} style={{fontWeight: "bold"}}><Text>Global blend defaults:</Text></Row>
			<Row mt={2} style={{opacity: 0.7, fontSize: 11}}>
				<Text>Fall-back values for a path's material feature when its per-path blend fields are
					absent (see each path's Material tab).</Text>
			</Row>
			<RowLR mt={5} splitAt={150}>
				<TextPlus info={`
					Default blend-band half-width in metres at a region's boundary (used when a path
					feature leaves its feather unset).  0 = razor-sharp edge; larger = a softer transition.
				`.AsMultiline(0)}>Default feather (m):</TextPlus>
				<Row>
					<Spinner ml={5} step="any" min={0} value={data.blendDefaults.feather}
						onChange={val=>ChangeBlend(b=>b.feather = val != null && val >= 0 ? val : 0)}/>
					<Button ml={5} mdIcon="delete" title="Reset to default (2)" onClick={()=>ChangeBlend(b=>b.feather = 2)}/>
				</Row>
			</RowLR>
			<RowLR mt={5} splitAt={150}>
				<TextPlus info={`
					Default falloff curve inside the feather band (used when a path feature leaves its
					feather-curve unset).  Smooth = the classic smoothstep; Linear = a straight ramp;
					Step = instant (same as feather 0).
				`.AsMultiline(0)}>Default feather curve:</TextPlus>
				<Row>
					<Select displayType="button bar" options={[
						{name: "Smooth", value: "smooth"},
						{name: "Linear", value: "linear"},
						{name: "Step", value: "step"},
					]} value={data.blendDefaults.featherCurve ?? null}
						onChange={val=>ChangeBlend(b=>b.featherCurve = val ?? undefined)}/>
					<Button ml={5} mdIcon="delete" title="Clear (inherit a smoother parent / default)" enabled={data.blendDefaults.featherCurve != null}
						onClick={()=>ChangeBlend(b=>b.featherCurve = undefined)}/>
				</Row>
			</RowLR>
			<Row mt={4} style={{opacity: 0.6, fontSize: 11}}>
				<Text>Tip: per-path values (path → Material tab) override these when set; "map default" in the path UI means "use these".</Text>
			</Row>
		</Column>
	);
});